import { useMemo } from "react";
import { useSpring, useSprings, animated } from "@react-spring/web";
import Tooltip from "@mui/material/Tooltip";
import { fontSizes } from "../../typography";
import type { Figure2CDProps, LayoutResult, TreeData } from "./types";
import treeData from "../../../data/figure2tree.json";
import { computeLayout, LABELS, PALETTE, MARGINS } from "./layout";
import { LeftmostNodeLabel, RightmostNodeLabel } from "./Labels";
import { LinkCurve } from "./LinkCurve";
import circuit from "../../../assets/circuit.svg";

const data: TreeData = treeData as TreeData;

// --- Component --------------------------------------------------------------
export default function Figure2CD({
  svgWidth,
  svgHeight,
  currentStep,
  isNarrow,
}: Figure2CDProps & { isNarrow: boolean }) {
  const { linePositions, nodePositions, linkPaths } =
    useMemo<LayoutResult>(() => {
      const effectiveWidth = svgWidth;
      return data
        ? computeLayout(data, effectiveWidth, svgHeight, isNarrow)
        : { linePositions: [], nodePositions: [], linkPaths: [] };
    }, [svgWidth, svgHeight, isNarrow]);

  // Map for quick node lookup by id in render phase
  const nodeMap = useMemo(
    () => new Map(nodePositions.map((n) => [n.id, n])),
    [nodePositions],
  );

  const isAnimating = currentStep === "mapping-intro";

  // Fade-in spring for all non-node/non-link elements (header, lines, labels, title)
  const elementFadeSpring = useSpring({
    from: { opacity: 0 },
    to: { opacity: 1 },
    config: { duration: 400 },
    immediate: !isAnimating,
  });

  // Link animation springs per source level (0,1,2)
  // Add 400ms delay to allow header/lines to fade in first
  const linkDelays = [400, 1900, 3400];
  const linkSprings = useSprings(
    linkDelays.length,
    linkDelays.map((delay) => ({
      config: { duration: 1500 },
      from: { value: 1 },
      to: { value: isAnimating ? 0 : 1 },
      delay: isAnimating ? delay : 0,
      immediate: !isAnimating,
    })),
  );

  // Node opacity springs - appear after preceding link groups
  // Add 400ms delay to sync with link animation start
  const nodeDelays = [400, 1900, 3400, 4900];
  const nodeOpacitySprings = useSprings(
    nodeDelays.length,
    nodeDelays.map((delay) => ({
      config: { duration: 300 },
      from: { opacity: 0 },
      to: { opacity: isAnimating ? 1 : 0 },
      delay: isAnimating ? delay : 0,
      immediate: !isAnimating,
    })),
  );

  const highlightedNodeIds = useMemo(
    () => new Set(["854231", "854229", "854230", "854219_2"]),
    [],
  );

  if (!linePositions.length) return null;

  const leftmostNode =
    nodePositions.find((n) => n.level === 0) ||
    nodePositions.reduce((min, n) => (n.x < min.x ? n : min), nodePositions[0]);

  // Define highlighted nodes and links
  const isCompoundingLosses =
    currentStep === "mapping-issues" || currentStep === "compounding-losses";

  const nodeRadius = svgWidth < 500 ? 6 : 10;

  // Scale down the entire graphic when very narrow to prevent text cutoff
  const scale = svgWidth < 430 ? 430 / svgWidth : 1;
  const scaledHeight = svgHeight * scale;
  const svgYOffset = svgWidth < 500 ? -20 : 0;

  return (
    <div style={{ overflow: "auto", width: svgWidth, height: svgHeight }}>
      <svg
        width={svgWidth * scale}
        height={scaledHeight}
        style={{
          transformOrigin: "0 0",
          display: "block",
          translate: `0 ${svgYOffset}px`,
        }}
      >
        {/* Column labels - visible early */}
        {linePositions.map((x, i) => (
          <animated.text
            key={`label-${i}`}
            x={x}
            y={MARGINS.top - 20}
            textAnchor="middle"
            fontSize={
              isNarrow
                ? fontSizes.figure2CDLabelsNarrow
                : fontSizes.figure2CDLabels
            }
            fill={PALETTE[i]}
            fontWeight={600}
            style={{ opacity: elementFadeSpring.opacity }}
          >
            {LABELS[i]}
          </animated.text>
        ))}

        {/* Vertical lines - animate in on transition */}
        {linePositions.map((x, i) => {
          return (
            <animated.line
              key={`line-${LABELS[i]}`}
              x1={x}
              x2={x}
              y1={MARGINS.top}
              y2={svgHeight - MARGINS.bottom}
              stroke="#2C2C2C"
              strokeWidth={1}
              strokeDasharray="4 4"
              style={{ opacity: elementFadeSpring.opacity }}
            />
          );
        })}

        {/* Links */}
        {linkPaths.map((link, _i) => {
          const shouldHighlight = !isCompoundingLosses || link.step === "UN";
          const linkOpacity = shouldHighlight ? 1 : 0.2;
          const sourceLevel = nodeMap.get(link.source)?.level ?? 0;
          const linkSpring = linkSprings[Math.min(sourceLevel, 2)];
          const showArrow = isAnimating;
          return (
            <g
              key={`link-${link.source}-${link.target}`}
              opacity={linkOpacity}
              style={{ transition: "opacity 0.5s ease-in-out" }}
            >
              <LinkCurve
                link={link}
                spring={linkSpring}
                showArrow={showArrow}
              />
            </g>
          );
        })}

        {/* Nodes */}
        {nodePositions.map((node) => {
          const shouldHighlight =
            !isCompoundingLosses || highlightedNodeIds.has(node.id);
          const highlightOpacity = shouldHighlight ? 1 : 0.2;
          const nodeSpring = nodeOpacitySprings[node.level];

          const showTooltip = node.level !== 0 && node.level !== 3;

          return (
            <Tooltip
              key={`tooltip-${node.id}`}
              title={
                <div
                  style={{
                    fontSize: 12,
                    whiteSpace: "pre-line",
                    lineHeight: 1.3,
                  }}
                >
                  {`${node.code}\n${node.name}`}
                </div>
              }
              placement="right"
              arrow
              enterDelay={0}
              leaveDelay={0}
              disableHoverListener={!showTooltip}
            >
              {isAnimating ? (
                <animated.g
                  opacity={nodeSpring.opacity.to((o) => o * highlightOpacity)}
                  style={{
                    cursor: nodeSpring.opacity.to((o) =>
                      o * highlightOpacity >= 0.05 ? "pointer" : "default",
                    ),
                    pointerEvents: nodeSpring.opacity.to((o) =>
                      o * highlightOpacity >= 0.05 ? "auto" : "none",
                    ),
                  }}
                >
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={nodeRadius}
                    fill={PALETTE[node.level]}
                  />
                </animated.g>
              ) : (
                <g
                  opacity={highlightOpacity}
                  style={{
                    transition: "opacity 0.5s ease-in-out",
                    cursor: highlightOpacity >= 0.05 ? "pointer" : "default",
                    pointerEvents: highlightOpacity >= 0.05 ? "auto" : "none",
                  }}
                >
                  <circle
                    cx={node.x}
                    cy={node.y}
                    r={nodeRadius}
                    fill={PALETTE[node.level]}
                  />
                </g>
              )}
            </Tooltip>
          );
        })}

        {/* Leftmost label with image */}
        {leftmostNode && (
          <g>
            {(() => {
              const gap = isNarrow ? 28 : 80;
              const labelX = leftmostNode.x - gap;
              const imageSize = isNarrow ? 50 : 80;
              const imageY = leftmostNode.y - (isNarrow ? 90 : 140);
              // In compact mode: right-align image with label's right edge
              // In wide mode: center image on label
              const imageX = isNarrow
                ? labelX - imageSize / 1.1
                : labelX - imageSize / 2;
              return (
                <image
                  href={circuit}
                  x={imageX}
                  y={imageY}
                  width={imageSize}
                  height={imageSize}
                  preserveAspectRatio="xMidYMid meet"
                />
              );
            })()}
            <LeftmostNodeLabel node={leftmostNode} compact={isNarrow} />
          </g>
        )}

        {/* Rightmost labels */}
        {nodePositions
          .filter((n) => n.level === 3)
          .map((node, idx) => {
            const shouldHighlight =
              !isCompoundingLosses || highlightedNodeIds.has(node.id);
            const highlightOpacity = shouldHighlight ? 1 : 0.2;
            const spring = nodeOpacitySprings[3];
            // Add extra vertical spacing on narrow screens + move all up a bit
            const yOffset = isNarrow ? -15 + idx * 15 : 0;
            const transformedNode = { ...node, y: node.y + yOffset };
            return isAnimating ? (
              <animated.g
                key={`label-${node.id}`}
                opacity={spring.opacity.to((o) => o * highlightOpacity)}
              >
                <RightmostNodeLabel node={transformedNode} compact={isNarrow} />
              </animated.g>
            ) : (
              <g
                key={`label-${node.id}`}
                opacity={highlightOpacity}
                style={{ transition: "opacity 0.5s ease-in-out" }}
              >
                <RightmostNodeLabel node={transformedNode} compact={isNarrow} />
              </g>
            );
          })}

        {/* Title */}
        {
          <animated.text
            x={svgWidth / 2}
            y={MARGINS.top / 2}
            textAnchor="middle"
            fontSize={fontSizes.vizTitle2}
            fill="rgb(77,77,77)"
            fontWeight={600}
            style={{ opacity: elementFadeSpring.opacity }}
          >
            {isCompoundingLosses
              ? "Comtrade Mapping of Products"
              : "WCO Mapping of Products"}
          </animated.text>
        }
      </svg>
    </div>
  );
}
